import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsMetricsKeys} from '../../../models/api/analyticsMetricsModel';
import {AnalyticsSelectedMetricsModel} from '../../../models/api/analyticsSavedViewModel';
import {
  getCompanyHasAccountsAnalytics,
  getCompanyHasConversationStagesFeature,
} from '../../../store/data/company/companySelectors';
import {getAnalyticsReportType} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {fetchAnalyticsMetrics} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {updateAnalyticsSelectedMetricsAction} from '../../../store/panels/analytics/report/analyticsPanelReportActionsWithRedirect';
import {
  getAnalyticsMetricsToFetch,
  getAnalyticsReportJobs,
  getAnalyticsSelectedMetrics,
  getConversationResolutionStrategy,
} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {AnalyticsReportJobPrioritiesEnum} from '../../../store/panels/analytics/report/analyticsPanelReportState';
import {getReportBaseOptions} from '../reportHelpers/analyticsReportHelpers';
import {AnalyticsReportsEnum, ReportElementIdsEnum} from '../reportHelpers/reportTypes';

/**
 * Custom hook to update the selected metrics.
 * @deprecated Replaced by useAnalyticsSelectedMetrics for the v3.
 */
export const useAnalyticsUpdateSelectedMetrics = () => {
  const dispatch = useAppDispatch();
  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);
  const jobs = useAppSelector(getAnalyticsReportJobs);
  const {hiddenMetrics} = useAppSelector(getAnalyticsMetricsToFetch);
  const reportType = useAppSelector(getAnalyticsReportType);
  const hasConversationStagesFeature = useAppSelector(getCompanyHasConversationStagesFeature);
  const hasAccountsAnalyticsFeature = useAppSelector(getCompanyHasAccountsAnalytics);
  const conversationResolutionStrategy = useAppSelector(getConversationResolutionStrategy);

  const updateSelectedMetrics = (metrics: ReadonlyArray<string>, reportElementId: ReportElementIdsEnum) => {
    const payload: AnalyticsSelectedMetricsModel = {
      ...selectedMetrics,
      [reportElementId]: metrics,
    };

    dispatch(updateAnalyticsSelectedMetricsAction(payload));
  };

  // This method will potentially fetch hidden metrics, we pass the newly selected metric to check if we need to fetch them or not.
  const maybeFetchHiddenMetrics = (newlySelectedMetric: AnalyticsMetricsKeys) => {
    // Knowledge base has no hidden metrics as of right now.
    if (reportType === AnalyticsReportsEnum.KNOWLEDGE_BASE) {
      return;
    }
    // Do we already have a job containing this metric the user just selected?
    if (jobs.some((job) => job.metrics.includes(newlySelectedMetric))) {
      return;
    } // Then it's all good - it's either already fetched or being fetched.

    // Otherwise it means we need to fetch the hidden metrics.
    const options = getReportBaseOptions({
      reportType,
      metrics: hiddenMetrics,
      hasAccountsAnalyticsFeature,
      hasConversationStagesFeature,
      conversationResolutionStrategy,
    });
    dispatch(fetchAnalyticsMetrics(hiddenMetrics, AnalyticsReportJobPrioritiesEnum.SECONDARY, options));
  };

  return {updateSelectedMetrics, maybeFetchHiddenMetrics};
};
